import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from 'react';
import type { AdminCapability } from '@kopechka/contracts';
import { resolveBaseUrl, type ApiResult } from '../../api/client.js';
import {
  SUPPORT_PANEL_API_BASE_URL,
  SUPPORT_PANEL_KEY_NAME_MAX,
  createSupportPanelKey,
  listSupportPanelKeys,
  revokeSupportPanelKey,
  sortSupportPanelKeys,
  validateSupportPanelKeyName,
  type SupportPanelKey,
} from '../../api/support-panel-keys.js';
import { useCan } from '../../session/can.js';
import { useSession } from '../../session/session-context.js';
import {
  Badge,
  Banner,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  Input,
  Modal,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TRow,
} from '../../ui/index.js';

/**
 * Contract C3 capability. Cast so this compiles whether or not the shared
 * `AdminCapability` union already lists it; the server session is the authority.
 */
export const CHAT_INTEGRATIONS_CAPABILITY = 'chat:integrations' as AdminCapability;

/**
 * `/messages` — "Destek paneli bağlantısı" (2026-09-23).
 *
 * Lets an admin issue / revoke the bearer keys the Telegram support (agent)
 * panel uses to read and answer the site chat through
 * `/integrations/support-chat/v1`. Collapsed by default and placed under the
 * chat grid so it never shifts the inbox or the thread pane.
 *
 *  - Rendered only with `chat:integrations`; a 401/403/404 on the first list
 *    read (capability revoked server-side, API not deployed yet) hides it
 *    silently. Once the card has been shown, the same answers are reported in
 *    the card instead: a later hide could otherwise vanish an open token reveal.
 *  - One list read on mount (visibility probe), one on each expand, one after
 *    every create/revoke and on "Yenile". It never polls, so the page's 5 s
 *    chat polling is untouched.
 *  - The raw token exists only inside {@link CreateKeyDialog} while it is open:
 *    it is not kept in any mutation hook, parent state, storage or log.
 */
export function SupportPanelKeysCard(): JSX.Element | null {
  const allowed = useCan(CHAT_INTEGRATIONS_CAPABILITY);
  return allowed ? <SupportPanelKeysPanel /> : null;
}

type KeysState =
  | { readonly kind: 'probing' }
  | { readonly kind: 'hidden' }
  | { readonly kind: 'error'; readonly reason: string; readonly text: string }
  | { readonly kind: 'ok'; readonly keys: readonly SupportPanelKey[] };

/** 401 / 403 / 404: this admin has no such surface — on the first read, disappear without noise. */
function isSilentMiss(res: ApiResult<unknown>): boolean {
  if (res.kind === 'forbidden' || res.kind === 'auth_required') return true;
  return res.kind === 'error' && res.status === 404;
}

function failureReason(res: ApiResult<unknown>): string {
  switch (res.kind) {
    case 'ok':
      return 'ok';
    case 'error':
      return res.reason;
    case 'network_error':
      return `network:${res.message}`;
    case 'forbidden':
      return 'forbidden';
    case 'auth_required':
      return 'auth_required';
  }
}

const NAME_ERROR_CODES = new Set([
  'bad_request',
  'invalid_name',
  'name_required',
  'name_too_long',
  'invalid_body',
  'validation_failed',
]);

/** Turkish operator text for a failed list / create / revoke call. */
export function supportPanelKeyErrorText(
  res: ApiResult<unknown>,
  op: 'list' | 'create' | 'revoke',
): string {
  switch (res.kind) {
    case 'ok':
      return '';
    case 'forbidden':
      return 'Yetki yok (chat:integrations).';
    case 'auth_required':
      return 'Oturumun süresi doldu; yeniden giriş yapın.';
    case 'network_error':
      return op === 'create'
        ? 'Sunucuya ulaşılamadı. Anahtar yine de oluşmuş olabilir: listede elinizde olmayan bir anahtar görürseniz iptal edip yenisini oluşturun.'
        : 'Sunucuya ulaşılamadı. Bağlantınızı kontrol edip tekrar deneyin.';
    case 'error': {
      if (res.status === 429 || res.reason === 'rate_limited') {
        return 'Çok fazla deneme yapıldı; biraz sonra tekrar deneyin.';
      }
      if (op === 'create' && NAME_ERROR_CODES.has(res.reason)) {
        return `Ad 1–${SUPPORT_PANEL_KEY_NAME_MAX} karakter olmalı.`;
      }
      if (op === 'create' && res.reason === 'invalid_create_payload') {
        return 'Sunucu yanıtında anahtar yok. Anahtar oluşmuş olabilir: listede görünüyorsa iptal edip yenisini oluşturun.';
      }
      if (op === 'revoke' && (res.status === 404 || res.reason === 'not_found')) {
        return 'Anahtar bulunamadı; liste yenilendi.';
      }
      const verb =
        op === 'create'
          ? 'Anahtar oluşturulamadı'
          : op === 'revoke'
            ? 'Anahtar iptal edilemedi'
            : 'Anahtarlar yüklenemedi';
      return `${verb} (${res.reason}).`;
    }
  }
}

function useSupportPanelKeys(): {
  readonly state: KeysState;
  readonly refreshError: string | null;
  readonly loading: boolean;
  readonly reload: () => void;
} {
  const session = useSession();
  const hasSession = session.state.kind === 'cookie_session';
  const [state, setState] = useState<KeysState>({ kind: 'probing' });
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);
  const hasDataRef = useRef(false);
  // True once the card is on screen (a list or an in-card error). From then on
  // a 401/403/404 is a visible failure, never a silent disappearance.
  const shownRef = useRef(false);
  const reload = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    if (!hasSession) {
      hasDataRef.current = false;
      shownRef.current = false;
      setState({ kind: 'hidden' });
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    const settle = (res: ApiResult<{ readonly keys: readonly SupportPanelKey[] }>): void => {
      if (controller.signal.aborted) return;
      setLoading(false);
      if (res.kind === 'ok') {
        hasDataRef.current = true;
        shownRef.current = true;
        setRefreshError(null);
        setState({ kind: 'ok', keys: sortSupportPanelKeys(res.data.keys) });
        return;
      }
      if (isSilentMiss(res) && !shownRef.current) {
        setRefreshError(null);
        setState({ kind: 'hidden' });
        return;
      }
      const text = supportPanelKeyErrorText(res, 'list');
      // Never silently stale: keep the last list visible but say it is old.
      if (hasDataRef.current) {
        setRefreshError(text);
      } else {
        shownRef.current = true;
        setState({ kind: 'error', reason: failureReason(res), text });
      }
    };
    listSupportPanelKeys({ baseUrl: resolveBaseUrl(), signal: controller.signal })
      .then(settle)
      .catch((err: unknown) =>
        settle({ kind: 'network_error', message: err instanceof Error ? err.message : String(err) }),
      );
    return () => controller.abort();
  }, [hasSession, tick]);

  return { state, refreshError, loading, reload };
}

function SupportPanelKeysPanel(): JSX.Element | null {
  const { state, refreshError, loading, reload } = useSupportPanelKeys();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const bodyId = useId();

  // Rendered beside the card (the scrim is position: fixed), keyed, in the same
  // slot of both returns: the dialog, and an open one-time token, keep their
  // identity even if the card itself goes away (e.g. the session ends).
  const dialog = creating ? (
    <CreateKeyDialog key="support-panel-create-key" onClose={() => setCreating(false)} onCreated={reload} />
  ) : null;

  // Nothing is shown until the first read answers, so a 403/404 never flashes.
  if (state.kind === 'probing' || state.kind === 'hidden') {
    return (
      <>
        {null}
        {dialog}
      </>
    );
  }

  const keys = state.kind === 'ok' ? state.keys : [];
  const activeCount = keys.filter((k) => k.revokedAt === null).length;
  const subtitle =
    state.kind === 'error'
      ? 'Anahtarlar yüklenemedi'
      : activeCount > 0
        ? `${activeCount} aktif anahtar`
        : 'Aktif anahtar yok';

  function toggle(): void {
    const next = !open;
    setOpen(next);
    if (next) reload(); // fresh "son kullanım" whenever the card is opened
  }

  async function revoke(key: SupportPanelKey): Promise<void> {
    if (revokingId !== null) return;
    const ok = window.confirm(
      `"${key.name}" anahtarı iptal edilecek. Bu anahtarı kullanan temsilci paneli site sohbetine artık bağlanamaz. Bu işlem geri alınamaz. Devam edilsin mi?`,
    );
    if (!ok) return;
    setRevokingId(key.id);
    setActionError(null);
    try {
      const res = await revokeSupportPanelKey({ baseUrl: resolveBaseUrl() }, key.id);
      if (res.kind !== 'ok') setActionError(supportPanelKeyErrorText(res, 'revoke'));
    } catch (err: unknown) {
      setActionError(
        supportPanelKeyErrorText(
          { kind: 'network_error', message: err instanceof Error ? err.message : String(err) },
          'revoke',
        ),
      );
    } finally {
      setRevokingId(null);
      reload();
    }
  }

  return (
    <>
      <Card flush data-testid="support-panel-keys-card">
        <CardHeader
          title="Destek paneli bağlantısı"
          subtitle={subtitle}
          actions={
            <Button
              variant="ghost"
              size="sm"
              aria-expanded={open}
              aria-controls={open ? bodyId : undefined}
              onClick={toggle}
              icon={open ? <Icon.Chevron size={14} /> : <Icon.ChevronRight size={14} />}
            >
              {open ? 'Gizle' : 'Göster'}
            </Button>
          }
        />
        {open ? (
          <CardBody id={bodyId}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
              <p className="t-xs t-tertiary" style={{ margin: 0 }}>
                Telegram temsilci panelini bu site sohbetine bağlar: panel müşteri mesajlarını buradan okur,
                temsilcilerin yanıtları buraya yazılır.
                <br />
                Bir anahtar oluşturun ve API adresiyle birlikte temsilci panelinin yönetim sayfasına girin;
                kullanılmayan ya da sızan anahtarı iptal edin.
              </p>

              <div style={{ minWidth: 0 }}>
                <div className="field-label">API adresi</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
                  <code data-testid="support-panel-base-url" style={{ overflowWrap: 'anywhere' }}>
                    {SUPPORT_PANEL_API_BASE_URL}
                  </code>
                  <CopyTextButton value={SUPPORT_PANEL_API_BASE_URL} label="API adresini kopyala" subject="API adresi" />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button
                  variant="primary"
                  size="sm"
                  icon={<Icon.Key size={14} />}
                  onClick={() => {
                    setActionError(null);
                    setCreating(true);
                  }}
                >
                  Anahtar oluştur
                </Button>
                <Button variant="ghost" size="sm" icon={<Icon.Refresh size={14} />} loading={loading} onClick={reload}>
                  Yenile
                </Button>
              </div>

              {refreshError ? (
                <Banner tone="warn">Anahtar listesi güncellenemedi: {refreshError} Son bilinen liste gösteriliyor.</Banner>
              ) : null}
              {actionError ? <Banner tone="danger">{actionError}</Banner> : null}

              {state.kind === 'error' ? (
                <ErrorState reason={state.reason} hint={state.text} onRetry={reload} />
              ) : keys.length === 0 ? (
                <EmptyState
                  label="Henüz anahtar yok"
                  hint="Temsilci panelini bağlamak için bir anahtar oluşturun."
                />
              ) : (
                <div className="tbl-wrap" style={{ overflowX: 'auto' }}>
                  <Table dense>
                    <THead>
                      <TRow>
                        <TH>Ad</TH>
                        <TH>Parmak izi</TH>
                        <TH>Oluşturulma</TH>
                        <TH>Son kullanım</TH>
                        <TH>Durum</TH>
                        <TH>
                          <span className="sr-only">İşlem</span>
                        </TH>
                      </TRow>
                    </THead>
                    <TBody>
                      {keys.map((k) => (
                        <TRow key={k.id} data-testid={`support-panel-key-${k.id}`}>
                          <TD style={{ overflowWrap: 'anywhere' }}>{k.name}</TD>
                          <TD>
                            <code>{k.fingerprint || '—'}</code>
                          </TD>
                          <TD>
                            {fmtDateTime(k.createdAt)}
                            {k.createdBy ? <div className="t-xs t-tertiary">{k.createdBy}</div> : null}
                          </TD>
                          <TD>
                            {k.lastUsedAt ? (
                              fmtDateTime(k.lastUsedAt)
                            ) : (
                              <span className="t-tertiary">Hiç kullanılmadı</span>
                            )}
                          </TD>
                          <TD>
                            {k.revokedAt ? (
                              <>
                                <Badge tone="neutral">İptal edildi</Badge>
                                <div className="t-xs t-tertiary">{fmtDateTime(k.revokedAt)}</div>
                              </>
                            ) : (
                              <Badge tone="success">Aktif</Badge>
                            )}
                          </TD>
                          <TD style={{ textAlign: 'right' }}>
                            {k.revokedAt ? null : (
                              <Button
                                variant="danger"
                                size="sm"
                                title={`"${k.name}" anahtarını iptal et`}
                                loading={revokingId === k.id}
                                disabled={revokingId !== null && revokingId !== k.id}
                                onClick={() => void revoke(k)}
                              >
                                İptal et
                              </Button>
                            )}
                          </TD>
                        </TRow>
                      ))}
                    </TBody>
                  </Table>
                </div>
              )}
            </div>
          </CardBody>
        ) : null}
      </Card>
      {dialog}
    </>
  );
}

/**
 * Name prompt → one-time token reveal. The token lives only in this
 * component's state; closing the dialog clears it and the parent unmounts it.
 */
function CreateKeyDialog({
  onClose,
  onCreated,
}: {
  readonly onClose: () => void;
  readonly onCreated: () => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<{ readonly token: string; readonly key: SupportPanelKey } | null>(null);
  const [copied, setCopied] = useState(false);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);
  const nameRef = useRef<HTMLInputElement>(null);
  const inputId = useId();

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Not `autoFocus`: Modal focuses its close button in a passive effect, which
  // runs after React's commit-time autoFocus. This parent effect runs after
  // Modal's, so the name field ends up focused.
  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const nameError = validateSupportPanelKeyName(name);

  async function submit(e?: FormEvent): Promise<void> {
    e?.preventDefault();
    setTouched(true);
    if (nameError !== null || inFlightRef.current) return;
    inFlightRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const res = await createSupportPanelKey({ baseUrl: resolveBaseUrl() }, name.trim());
      if (res.kind === 'ok') onCreated();
      // Dialog already gone: drop the token on the floor, never park it anywhere.
      if (!mountedRef.current) return;
      if (res.kind === 'ok') setIssued({ token: res.data.token, key: res.data.key });
      else setError(supportPanelKeyErrorText(res, 'create'));
    } catch (err: unknown) {
      if (mountedRef.current) {
        setError(
          supportPanelKeyErrorText(
            { kind: 'network_error', message: err instanceof Error ? err.message : String(err) },
            'create',
          ),
        );
      }
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setSubmitting(false);
    }
  }

  function close(): void {
    // Closing mid-request would lose the only copy of the token.
    if (submitting || inFlightRef.current) return;
    if (
      issued &&
      !copied &&
      !window.confirm('Anahtarı henüz kopyalamadınız. Kapatırsanız bir daha gösterilmeyecek. Yine de kapatılsın mı?')
    ) {
      return;
    }
    setIssued(null);
    onClose();
  }

  return (
    <Modal
      open
      onClose={close}
      title={issued ? 'Yeni destek paneli anahtarı' : 'Anahtar oluştur'}
      width={560}
      closeOnScrimClick={!issued && !submitting}
      testId="support-panel-key-dialog"
      footer={
        issued ? (
          <Button variant="primary" onClick={close}>
            Anahtarı sakladım, kapat
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={close} disabled={submitting}>
              Vazgeç
            </Button>
            <Button variant="primary" loading={submitting} onClick={() => void submit()}>
              Oluştur
            </Button>
          </>
        )
      }
    >
      {issued ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
          <Banner tone="warn" title="Bu anahtar bir daha gösterilmeyecek; temsilci panelinin yönetim sayfasına yapıştırın.">
            Kopechka yalnız anahtarın özetini saklar. Kaybolursa bu anahtarı iptal edip yenisini oluşturun.
          </Banner>
          <div style={{ minWidth: 0 }}>
            <div className="field-label">API anahtarı ({issued.key.name})</div>
            <code
              data-testid="support-panel-token"
              style={{ display: 'block', padding: '8px 10px', wordBreak: 'break-all', userSelect: 'all' }}
            >
              {issued.token}
            </code>
            <div style={{ marginTop: 8 }}>
              <CopyTextButton
                value={issued.token}
                label="Anahtarı kopyala"
                subject="Anahtar"
                onCopied={() => setCopied(true)}
              />
            </div>
          </div>
          <div className="t-xs t-tertiary" style={{ overflowWrap: 'anywhere' }}>
            API adresi: <code>{SUPPORT_PANEL_API_BASE_URL}</code>
          </div>
          <div className="t-xs t-tertiary">
            Parmak izi: <code>{issued.key.fingerprint || '—'}</code> — temsilci panelinde “Bağlantıyı test et”
            dediğinizde aynı değer görünmelidir.
          </div>
        </div>
      ) : (
        <form onSubmit={(e) => void submit(e)} style={{ minWidth: 0 }}>
          <Field
            label="Anahtar adı"
            htmlFor={inputId}
            error={touched ? nameError : null}
            help={`Anahtarın hangi panel için olduğunu anlatan kısa bir ad (en fazla ${SUPPORT_PANEL_KEY_NAME_MAX} karakter).`}
          >
            <Input
              ref={nameRef}
              id={inputId}
              value={name}
              maxLength={SUPPORT_PANEL_KEY_NAME_MAX}
              placeholder="ör. Destek paneli"
              autoComplete="off"
              invalid={touched && nameError !== null}
              disabled={submitting}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          {error ? (
            <div style={{ marginTop: 10 }}>
              <Banner tone="danger">{error}</Banner>
            </div>
          ) : null}
        </form>
      )}
    </Modal>
  );
}

/**
 * Copy button whose result is accessible: the accessible name follows the
 * visible status, and an always-present polite live region announces it (the
 * one-time token flow depends on knowing whether the copy worked).
 */
function CopyTextButton({
  value,
  label,
  subject,
  onCopied,
}: {
  readonly value: string;
  /** Accessible name at rest, e.g. "Anahtarı kopyala". */
  readonly label: string;
  /** What is copied, for the announcement, e.g. "Anahtar". Never the value itself. */
  readonly subject: string;
  readonly onCopied?: () => void;
}): JSX.Element {
  const [status, setStatus] = useState<'idle' | 'copied' | 'failed'>('idle');
  const timer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  async function copy(): Promise<void> {
    try {
      if (!navigator.clipboard) throw new Error('clipboard_unavailable');
      await navigator.clipboard.writeText(value);
      setStatus('copied');
      onCopied?.();
    } catch {
      // Clipboard blocked (insecure context / permission): the text stays selectable.
      setStatus('failed');
    }
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setStatus('idle'), 2500);
  }

  const ariaLabel =
    status === 'copied'
      ? `${label}: kopyalandı`
      : status === 'failed'
        ? `${label}: kopyalanamadı, elle seçin`
        : label;
  const announcement =
    status === 'copied'
      ? `${subject} panoya kopyalandı.`
      : status === 'failed'
        ? `${subject} panoya kopyalanamadı; metni elle seçip kopyalayın.`
        : '';

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        aria-label={ariaLabel}
        title={label}
        icon={status === 'copied' ? <Icon.Check size={14} /> : <Icon.Copy size={14} />}
        onClick={() => void copy()}
      >
        {status === 'copied' ? 'Kopyalandı' : status === 'failed' ? 'Kopyalanamadı — elle seçin' : 'Kopyala'}
      </Button>
      <span
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        data-testid={`copy-status-${subject}`}
      >
        {announcement}
      </span>
    </>
  );
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('tr-TR');
}
